import { isApple } from "./lib/browser";
import { Keys } from "./lib/keyboard";

export interface KeyboardShortcut {
  shortcut: string;
  key: string;
  code: string;
  modifiers: KeyModifier[];
  label: string;
  isPressed: (event: KeyboardEvent) => boolean;
}

const systemModifiers = [
  Keys.Meta,
  Keys.Control,
  Keys.Alt,
  Keys.Shift,
];

const keyboardEventModifierKeys = {
  [Keys.Meta]: "metaKey",
  [Keys.Control]: "ctrlKey",
  [Keys.Alt]: "altKey",
  [Keys.Shift]: "shiftKey",
} as const;

type KeyModifier = typeof systemModifiers[number];

export function anyPressed(event: KeyboardEvent, shortcuts: KeyboardShortcut[]) {
  for (const s of shortcuts) {
    if (s.isPressed(event)) return true;
  }
  return false;
}

function toIndividualKeys(shortcut: string) {
  return shortcut.split(/[+_-]/g).map(i => i.trim());
}

function getShortcutDescriptor(shortcut: string) {
  const allKeys = toIndividualKeys(shortcut);
  const keys = allKeys.filter(key => !(systemModifiers as string[]).includes(key));
  const modifiers = allKeys.filter(k => (systemModifiers as string[]).includes(k)) as KeyModifier[];

  const key = keys[0];
  if (!key) {
    throw new Error(`Incorrect shortcut '${shortcut}': shortcut key is required`);
  }
  if (keys.length > 1) {
    throw new Error(`Incorrect shortcut '${shortcut}': Multi-key shortcuts aren't supported`);
  }

  return {
    key: key.length === 1 ? key.toLowerCase() : key,
    code: key.length === 1 ? `Key${key.toUpperCase()}` : key,
    modifiers,
  };
}

function normalize(shortcut: string) {
  const fixedMod = shortcut.replaceAll("Mod", isApple ? Keys.Meta : Keys.Control);
  const { key, modifiers } = getShortcutDescriptor(fixedMod);

  return (modifiers as string[]).concat([key]).join("-");
}

export function toLabel(shortcut: string) {
  let label = shortcut
    .replaceAll(Keys.ArrowLeft, "←")
    .replaceAll(Keys.ArrowUp, "↑")
    .replaceAll(Keys.ArrowRight, "→")
    .replaceAll(Keys.ArrowDown, "↓")
    .replaceAll(Keys.Comma, ",")
    .replaceAll(Keys.Period, ".")
    .replaceAll(Keys.Backslash, "\\")
    .replaceAll("Mod", isApple ? Keys.Meta : Keys.Control)
    .replaceAll("Digit", "");

  if (isApple) {
    label = label
      .replaceAll("-", "")
      .replaceAll("+", "")
      .replaceAll(Keys.Control, "⌃")
      .replaceAll(Keys.Meta, "⌘")
      .replaceAll(Keys.Alt, "⌥")
      .replaceAll(Keys.Shift, "⇧")
      .replaceAll(Keys.Delete, "⌫");
  } else {
    label = label
      .replaceAll("-", "+")
      .replaceAll(Keys.Control, "Ctrl")
      .replaceAll(Keys.Alt, "Alt")
      .replaceAll(Keys.Shift, "Shift");
  }

  return label;
}

interface ShortcutDescriptor {
  key: string;
  code: string;
  modifiers: KeyModifier[];
}

function exactGuard(event: KeyboardEvent, modifiers: KeyModifier[]) {
  return systemModifiers.some(m => {
    const prop = keyboardEventModifierKeys[m];
    return event[prop] && !modifiers.includes(m);
  });
}

function allModifiersPressed(event: KeyboardEvent, modifiers: KeyModifier[]) {
  return modifiers.every(m => {
    const prop = keyboardEventModifierKeys[m];
    return event[prop];
  });
}

function createIsPressed({ code, modifiers }: ShortcutDescriptor) {
  return (e: KeyboardEvent) => {
    const pressedCode = e.code.replaceAll("NumPad", "Digit");
    if (code !== pressedCode) return false;
    if (exactGuard(e, modifiers)) return false;
    return allModifiersPressed(e, modifiers);
  };
}

export function createKeyboardShortcut(shortcut: string): KeyboardShortcut {
  const normalized = normalize(shortcut);
  const label = toLabel(shortcut);
  const descriptor = getShortcutDescriptor(normalized);

  return {
    shortcut: normalized,
    key: descriptor.key,
    code: descriptor.code,
    modifiers: descriptor.modifiers,
    label,
    isPressed: createIsPressed(descriptor),
  };
}

export const ModBackslash = createKeyboardShortcut("Mod-Backslash");

export const Backspace = createKeyboardShortcut("Backspace");
export const ModBackspace = createKeyboardShortcut("Mod-Backspace");
export const Tab = createKeyboardShortcut("Tab");
export const ShiftTab = createKeyboardShortcut("Shift-Tab");

export const Bold = createKeyboardShortcut("Mod-B");
export const Italic = createKeyboardShortcut("Mod-I");
export const Underline = createKeyboardShortcut("Mod-U");
export const Strikethrough = createKeyboardShortcut("Mod-Shift-S");
export const InlineCode = createKeyboardShortcut("Mod-E");
export const Delete = createKeyboardShortcut("Delete");
export const ModDelete = createKeyboardShortcut("Mod-Delete");
export const Duplicate = createKeyboardShortcut("Mod-D");
export const MoveUp = createKeyboardShortcut("Alt-ArrowUp");
export const MoveDown = createKeyboardShortcut("Alt-ArrowDown");
export const MoveLeft = createKeyboardShortcut("Alt-ArrowLeft");
export const MoveRight = createKeyboardShortcut("Alt-ArrowRight");
export const InsertAbove = createKeyboardShortcut("Mod-Alt-ArrowUp");
export const InsertBelow = createKeyboardShortcut("Mod-Alt-ArrowDown");
export const SelectTextblockStart = createKeyboardShortcut("Alt-Shift-ArrowUp");
export const SelectTextblockEnd = createKeyboardShortcut("Alt-Shift-ArrowDown");
export const AddConnectorUp = createKeyboardShortcut("Mod-Alt-Shift-ArrowUp");
export const AddConnectorRight = createKeyboardShortcut("Mod-Alt-Shift-ArrowRight");
export const AddConnectorDown = createKeyboardShortcut("Mod-Alt-Shift-ArrowDown");
export const AddConnectorLeft = createKeyboardShortcut("Mod-Alt-Shift-ArrowLeft");

// ModK is intentionally duplicated for both link insertion and CommandK menu
// InsertLink takes precedence over CommandK when link insert is relevant, e.g. in text documents
export const InsertLink = createKeyboardShortcut("Mod-K");
export const ModK = createKeyboardShortcut("Mod-K");

export const CommentStart = createKeyboardShortcut("Mod-Alt-C");
export const ToggleCodeBlock = createKeyboardShortcut("Mod-Shift-C");
export const CopyLinkToBlock = createKeyboardShortcut("Mod-Alt-Shift-C");
export const CanvasOnly = createKeyboardShortcut("Mod-Digit3");
export const DocumentOnly = createKeyboardShortcut("Mod-Digit1");
export const DocumentAndCanvas = createKeyboardShortcut("Mod-Digit2");
export const SizePosition = createKeyboardShortcut("Mod-L");
export const ShowEmojiPanel = createKeyboardShortcut("Mod-Control-Space");

export const FillDropper = createKeyboardShortcut("I");
export const PreviewPrototype = createKeyboardShortcut("Period");

export const AddColumnBefore = createKeyboardShortcut("Control-Alt-ArrowLeft");
export const AddColumnAfter = createKeyboardShortcut("Control-Alt-ArrowRight");
export const AddRowBefore = createKeyboardShortcut("Control-Alt-ArrowUp");
export const AddRowAfter = createKeyboardShortcut("Control-Alt-ArrowDown");

export const Cut = createKeyboardShortcut("Mod-X");
export const Copy = createKeyboardShortcut("Mod-C");
export const Paste = createKeyboardShortcut("Mod-V");
export const PasteAndMatch = createKeyboardShortcut("Mod-Shift-V");
export const PasteAndReplace = createKeyboardShortcut("Control-Shift-Mod-V");
export const SelectAll = createKeyboardShortcut("Mod-A");

export const Enter = createKeyboardShortcut("Enter");
export const ArrowUp = createKeyboardShortcut("ArrowUp");
export const ArrowDown = createKeyboardShortcut("ArrowDown");
export const ArrowLeft = createKeyboardShortcut("ArrowLeft");
export const ArrowRight = createKeyboardShortcut("ArrowRight");

export const Undo = createKeyboardShortcut("Mod-Z");
export const Redo = isApple ? createKeyboardShortcut("Mod-Shift-Z") : createKeyboardShortcut("Mod-Y");
export const RedoAlt = isApple ? createKeyboardShortcut("Mod-Y") : createKeyboardShortcut("Mod-Shift-Z");

export const F12 = createKeyboardShortcut("F12");
export const Escape = createKeyboardShortcut("Escape");

export const BROWSER_SHORTCUTS = [Cut, Copy, Paste, PasteAndMatch, F12, SelectAll, Undo, Redo, InsertLink, ModK];
const CONNECTOR_SHORTCUTS = [AddConnectorUp, AddConnectorRight, AddConnectorDown, AddConnectorLeft];
const MOVE_SHORTCUTS = [MoveUp, MoveRight, MoveDown, MoveLeft];

export function isModShortcut(event: KeyboardEvent) {
  if (Escape.isPressed(event)) return true;
  if (anyPressed(event, CONNECTOR_SHORTCUTS)) return true;
  if (isApple && ShowEmojiPanel.isPressed(event)) return false; // we want to let the emoji panel through on mac.
  return ((isApple && event.metaKey) || event.ctrlKey) && (event.key.length === 1);
}

export function shouldSuppressCanvasShortcut(event: KeyboardEvent) {
  return !isApple && (event.key === "Alt" || anyPressed(event, MOVE_SHORTCUTS));
}
